// v1.9 P0: Spinner 原子组件
// 3 variants: dots / ring / bar
// 3 sizes: sm / md / lg
import { twMerge } from 'tailwind-merge';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg';
export type SpinnerVariant = 'dots' | 'ring' | 'bar';

export interface SpinnerProps {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  className?: string;
  /** a11y label; 默认 "加载中" */
  label?: string;
}

const SIZE_DIM: Record<SpinnerSize, number> = {
  xs: 12,
  sm: 16,
  md: 24,
  lg: 32,
};

const DOT_SIZE: Record<SpinnerSize, string> = {
  xs: 'w-1 h-1',
  sm: 'w-1.5 h-1.5',
  md: 'w-2 h-2',
  lg: 'w-2.5 h-2.5',
};

const BAR_HEIGHT: Record<SpinnerSize, string> = {
  xs: 'h-2',
  sm: 'h-3',
  md: 'h-5',
  lg: 'h-6',
};

const BAR_WIDTH: Record<SpinnerSize, string> = {
  xs: 'w-0.5',
  sm: 'w-0.5',
  md: 'w-1',
  lg: 'w-1.5',
};

// 用 <output> 语义元素 (HTML 原生 role="status"), 避免 biome useSemanticElements
export function Spinner({
  size = 'md',
  variant = 'ring',
  className,
  label = '加载中',
}: SpinnerProps) {
  if (variant === 'ring') {
    const dim = SIZE_DIM[size];
    return (
      <output
        aria-label={label}
        className={twMerge('inline-block', className)}
        style={{ width: dim, height: dim }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={dim}
          height={dim}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="animate-spin"
          role="img"
          aria-hidden
        >
          <title>{label}</title>
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
      </output>
    );
  }

  if (variant === 'dots') {
    return (
      <output aria-label={label} className={twMerge('inline-flex items-center gap-1', className)}>
        <span
          className={twMerge('rounded-full bg-current animate-pulse-dot', DOT_SIZE[size])}
          style={{ animationDelay: '0ms' }}
        />
        <span
          className={twMerge('rounded-full bg-current animate-pulse-dot', DOT_SIZE[size])}
          style={{ animationDelay: '200ms' }}
        />
        <span
          className={twMerge('rounded-full bg-current animate-pulse-dot', DOT_SIZE[size])}
          style={{ animationDelay: '400ms' }}
        />
      </output>
    );
  }

  // bar
  return (
    <output aria-label={label} className={twMerge('inline-flex items-end gap-0.5', className)}>
      <span
        className={twMerge(
          'bg-current rounded-sm animate-pulse-dot',
          BAR_WIDTH[size],
          BAR_HEIGHT[size],
        )}
        style={{ animationDelay: '0ms' }}
      />
      <span
        className={twMerge(
          'bg-current rounded-sm animate-pulse-dot',
          BAR_WIDTH[size],
          BAR_HEIGHT[size],
        )}
        style={{ animationDelay: '150ms' }}
      />
      <span
        className={twMerge(
          'bg-current rounded-sm animate-pulse-dot',
          BAR_WIDTH[size],
          BAR_HEIGHT[size],
        )}
        style={{ animationDelay: '300ms' }}
      />
    </output>
  );
}
